<template>
    <LayoutPage v-if="wall.uuid">
        <template #header>
            <LoveProjectHeader :actions="actions" @publish="handlePublish" />
        </template>

        <template #sidebar>
            <router-view />
        </template>

        <template #preview>
            <LoveProjectPreview />
        </template>

        <template #footer>
            <LoveProjectFooter :actions="actions" @publish="handlePublish" />
        </template>
    </LayoutPage>
    <!-- Love Creator  -->
    <LoveCreator v-if="isCreatorShown" />
    <!-- First steps Video -->
    <cHelpVideosAfterOnboarding v-else />
    <!-- Modal for changing SLUG -->
    <cModalUrl
        v-model:open="showUrlModal"
        v-model:slug="slug"
        :links="wallLinks"
        @updateUuid="changeUuid({ tool: 'love', uuid: wall.uuid, newUuid: slug })"
    />
    <!-- Modal for renaming -->
    <ModalInput
        v-model:open="showRenameModal"
        v-model:text="projectName"
        :modal-data="modalRenameData"
        @cta="handleRenameProject(projectName)"
        @cancel="showRenameModal = false"
    />
</template>

<script>
import { markRaw } from "vue";
import { LayoutPage } from "@easy/ui/layouts";
import LoveProjectHeader from "@/apps/love/project/LoveProjectHeader.vue";
import LoveProjectPreview from "@/apps/love/project/LoveProjectPreview.vue";
import LoveProjectFooter from "@/apps/love/project/LoveProjectFooter.vue";
import LoveCreator from "@/apps/love/project/onboarding/LoveCreator.vue";
import { makeLogger } from "@/shared/consola";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import {
    PencilLineIcon,
    ShareLinkCircleIcon,
    TrashBinIcon,
    InvisibleIcon,
    ImageIcon,
    AiIcon,
} from "@easy/ui/icons";
import cModalUrl from "@/shared/components/cModalUrl.vue";
import { ModalInput } from "@easy/ui/modals";
import { EventBus } from "@/shared/projects/event-bus";
import { LOVE_ROUTE_NAMES } from "@/apps/love/love.routes";
import { storeToRefs } from "pinia";
import { useLoveCreatorStore } from "@/apps/love/project/onboarding/love-creator.store";
import cHelpVideosAfterOnboarding from "@/shared/components/help/cHelpVideosAfterOnboarding.vue";

const logger = makeLogger("LoveProjectPage");

export default {
    components: {
        cHelpVideosAfterOnboarding,
        LayoutPage,
        LoveProjectHeader,
        LoveProjectPreview,
        LoveProjectFooter,
        LoveCreator,
        cModalUrl,
        ModalInput,
    },
    mounted() {
        this.$watch(() => this.$route.params.projectUuid, this.handleProjectChange, {
            immediate: true,
        });
        this.$watch(
            () => this.wall.name,
            (wallName) => {
                this.projectName = wallName || "";
                document.title = `Easylove | ${wallName}`;
            },
            { immediate: true }
        );
        // FIXME: walk around for now when (publish)
        EventBus.$on("askForChanges", (resolve) => {
            resolve(false);
        });
    },
    setup() {
        const loveCreatorStore = useLoveCreatorStore();
        const { isCreatorShown } = storeToRefs(loveCreatorStore);
        const { autoOpenCreator } = loveCreatorStore;
        return { isCreatorShown, autoOpenCreator };
    },
    data() {
        return {
            showUrlModal: false,
            showRenameModal: false,
            showDeleteModal: false,
            showShareModel: false,
            slug: this.$route.params.projectUuid,
            projectName: "",
        };
    },
    watch: {
        wallUuid: {
            handler(value) {
                if (!value) {
                    return;
                }
                this.autoOpenCreator(this.wall);
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState(["globalUrl"]),
        ...mapState("love", ["wall", "testimonials"]),
        ...mapGetters(["isPublished", "appUrl"]),
        wallUuid() {
            return this.wall.uuid;
        },
        actions() {
            return [
                this.testimonials.length
                    ? {
                          label: this.$t("shared.generate_image"),
                          icon: markRaw(ImageIcon),
                          handler: () => {
                              this.generateImage2();
                          },
                      }
                    : {},
                {
                    label: this.$t("shared.generate_from_scratch"),
                    icon: markRaw(AiIcon),
                    handler: () => {
                        this.toggleCreator(true);
                    },
                },
                {
                    label: this.$t("shared.rename"),
                    icon: markRaw(PencilLineIcon),
                    handler: () => {
                        this.showRenameModal = true;
                    },
                },
                {
                    label: this.$t("shared.change_url.title"),
                    icon: markRaw(ShareLinkCircleIcon),
                    handler: () => {
                        this.showUrlModal = true;
                    },
                },
                this.isPublished(this.wall)
                    ? {
                          label: this.$t("shared.unpublish"),
                          icon: markRaw(InvisibleIcon),
                          handler: () => {
                              this.unpublishLove();

                              logger.log("unpublish");
                          },
                      }
                    : {},
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    icon: markRaw(TrashBinIcon),
                    isDanger: true,
                    handler: async () => {
                        const decision = await this.showConfirmModal({
                            title: this.$t("shared.delete_project"),
                            description: this.$t("shared.delete_project_info"),
                            ctaLabel: this.$t("shared.delete"),
                        });
                        if (decision) {
                            await this.removeProject({ tool: "love", uuid: this.wall.uuid });
                        }
                    },
                },
            ];
        },
        wallLinks() {
            return [
                {
                    title: this.$t("love.urls.wall"),
                    url: `${this.globalUrl}/walls/<strong>${this.slug}</strong>`,
                },
                {
                    title: this.$t("love.urls.project"),
                    url: `${this.appUrl}/projects/<strong>${this.slug}</strong>`,
                },
                {
                    title: this.$t("love.urls.form"),
                    url: `${this.appUrl}/ask/<strong>${this.slug}</strong>`,
                },
            ];
        },
        modalRenameData() {
            return {
                title: this.$t("shared.rename"),
                inputLabel: this.$t("shared.project_name"),
                ctaLabel: this.$t("shared.save"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
    },
    methods: {
        ...mapActions("love", ["publishProject", "unpublishLove", "hostCode"]),
        ...mapActions("love/projects", ["loadProject"]),
        ...mapActions("project", ["changeUuid", "removeProject", "renameProject"]),
        ...mapActions("love/uploads", ["generateImage2"]),
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations(["showAlert"]),
        handleProjectChange(uuid, oldUuid) {
            if (uuid && uuid !== oldUuid) {
                this.loadProject(uuid);
            }
        },
        async handleRenameProject(newName) {
            // FIXME There is problem with a SNAPSHOT.
            // When you rename project snapshot updates and overrides current data :(
            const error = await this.renameProject({ tool: "love", project: this.wall, newName });
            if (!error) {
                this.showRenameModal = false;
            }
        },
        async handlePublish() {
            // @todo: Refactor in future (change placement of this logic)
            // right now is difficult to make both logic for /love and /love2 versions inside the publishProject store action
            const { $t, testimonials } = this;
            const hasAnyReviews = Boolean(testimonials?.length);
            const hasAnyUnpublishedReviews = testimonials?.some((review) => !review.isPublished);
            // const hasEmptyReviews = testimonials?.some(
            //     (review) =>
            //         !review.name || (!review.content && review.type !== "video" && !review._deleted)
            // );

            if (!hasAnyReviews) {
                const decision = await this.showConfirmModal({
                    title: $t("love.publish.no_reviews_message"),
                    description: $t("love.publish.no_reviews_details"),
                    ctaLabel: $t("love.publish.add_first_review"),
                    size: "lg",
                });
                if (decision) {
                    this.$router.push({
                        name: LOVE_ROUTE_NAMES.projectReviewsList,
                        query: { open: "create" },
                    });
                }
                return;
            }
            //if (hasEmptyReviews) {
            //    this.showAlert({
            //        type: "wrong",
            //        message: this.$t("love.publish.empty_review"),
            //    });
            //    return;
            //}
            if (hasAnyUnpublishedReviews) {
                const decision = await this.showConfirmModal({
                    title: $t("love.publish.unpublished_reviews_message"),
                    description: $t("love.publish.unpublished_reviews_details"),
                    ctaLabel: $t("love.publish.yes_publish_all"),
                    cancelLabel: $t("love.publish.no_publish_wall_only"),
                    size: "2xl",
                });
                this.hostCode(decision);
            } else {
                this.publishProject();
            }
        },
    },
};
</script>
